/**
 * Store SKU contract for Apple App Store and Google Play.
 *
 * `scripts/store-skus.json` is the checked-in release catalogue consumed by
 * the safe store-convergence scripts.  Keep this UI mirror deliberately small:
 * this module lists only products the installed app is allowed to discover,
 * while `catalog.test.ts` prevents it drifting from that store contract.
 *
 * A subscription entitlement is family-level.  It unlocks cloud migration and
 * sync only after the server validates the store transaction; this value is
 * never an app-local entitlement switch.
 */
export const SUBSCRIPTION_PRODUCTS = {
  FAMILY_MONTHLY: 'chorepop_family_monthly',
  APPLE_PRODUCTION_FAMILY_MONTHLY: 'chorepop_family_monthly_prod',
} as const;

export const SUBSCRIPTION_PRODUCT_IDS = [
  SUBSCRIPTION_PRODUCTS.FAMILY_MONTHLY,
  SUBSCRIPTION_PRODUCTS.APPLE_PRODUCTION_FAMILY_MONTHLY,
] as const;

export type SubscriptionStoreTarget = {
  store: 'apple' | 'google';
  environment: 'staging' | 'production';
};

export const CHOREPOP_FAMILY_MONTHLY = {
  pricePence: 299,
  currency: 'GBP',
  billingPeriod: 'P1M',
  fallbackPrice: '£2.99/month',
  title: 'ChorePop Family',
  description: 'Secure family cloud sync and multi-device access for your ChorePop family.',
} as const;

export type SubscriptionProductId = (typeof SUBSCRIPTION_PRODUCT_IDS)[number];

export function isKnownSubscriptionProduct(productId: string): productId is SubscriptionProductId {
  return (SUBSCRIPTION_PRODUCT_IDS as readonly string[]).includes(productId);
}

/**
 * Apple product identifiers are app-record scoped, so the production App
 * Store record cannot reuse the staging identifier. Google Play and both
 * staging apps continue to use the original identifier.
 */
export function subscriptionProductIdFor(target: SubscriptionStoreTarget): SubscriptionProductId {
  return target.store === 'apple' && target.environment === 'production'
    ? SUBSCRIPTION_PRODUCTS.APPLE_PRODUCTION_FAMILY_MONTHLY
    : SUBSCRIPTION_PRODUCTS.FAMILY_MONTHLY;
}

export function isSubscriptionProductForTarget(
  productId: string,
  target: SubscriptionStoreTarget,
): productId is SubscriptionProductId {
  return productId === subscriptionProductIdFor(target);
}

/**
 * The free trial is server timed by ChorePop and lasts exactly 30 days. Store
 * products must not add a second introductory period after that local trial.
 */
export const FREE_TRIAL_DAYS = 30;
